import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaQuestionCircle, FaCookie, FaUtensils, FaConciergeBell, 
         FaHamburger, FaPizzaSlice, FaSearch} from 'react-icons/fa';
import { BiRestaurant } from 'react-icons/bi';
import { MdRestaurantMenu, MdFoodBank, MdOutlineEmojiFoodBeverage} from 'react-icons/md';
import { GiBowlOfRice, GiSlicedBread, GiNoodles, GiTacos, GiFruitBowl, GiCupcake, GiMeat, 
         GiFrenchFries, GiChickenLeg, GiSushis, GiHotMeal, GiSandwich, GiSaltShaker, 
         GiFoodChain, GiCoffeeCup, GiTeapot, GiSodaCan, GiWaterBottle, GiMilkCarton, 
         GiWineGlass, GiBeerStein, GiGlassShot, GiIceCreamCone, GiCakeSlice, 
         GiChocolateBar, GiHoneycomb } from 'react-icons/gi';
import toast from 'react-hot-toast';
import Layout from '../../../components/admin/Layout';
import axios from '../../../utils/axios';
import { LanguageContext } from '../../../context/LanguageContext';
import { adminTranslations } from '../../../translations/admin';

const CategoryIndex = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        sortBy: 'order'
    });
    const { isRTL, language } = useContext(LanguageContext);

    const t = useCallback((key) => {
        return adminTranslations[language]?.[key] || key;
    }, [language]);

    // Icon mapping object
    const iconComponents = {
        'Rice Bowl': GiBowlOfRice,
        'Bread': GiSlicedBread,
        'Noodles': GiNoodles,
        'Tacos': GiTacos,
        'Fruit Bowl': GiFruitBowl,
        'Cupcake': GiCupcake,
        'Meat': GiMeat,
        'French Fries': GiFrenchFries,
        'Chicken': GiChickenLeg,
        'Sushi': GiSushis,
        'Hot Meal': GiHotMeal,
        'Sandwich': GiSandwich,
        'Salt': GiSaltShaker,
        'Food Chain': GiFoodChain,
        'Coffee': GiCoffeeCup,
        'Tea': GiTeapot,
        'Soda': GiSodaCan,
        'Water': GiWaterBottle,
        'Milk': GiMilkCarton,
        'Wine': GiWineGlass,
        'Beer': GiBeerStein,
        'Shot': GiGlassShot,
        'Ice Cream': GiIceCreamCone,
        'Cake': GiCakeSlice,
        'Cookie': FaCookie,
        'Chocolate': GiChocolateBar,
        'Honey': GiHoneycomb,
        'Restaurant': BiRestaurant,
        'Menu': MdRestaurantMenu,
        'Food Bank': MdFoodBank,
        'Food Beverage': MdOutlineEmojiFoodBeverage,
        'Utensils': FaUtensils,
        'Service Bell': FaConciergeBell,
        'Hamburger': FaHamburger,
        'Pizza': FaPizzaSlice
    };

    // Effect for initial categories fetch
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);
                const response = await axios.get('/categories');
                setCategories(response.data.data);
            } catch (err) {
                toast.error(t('errorLoadingCategories'));
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, [t]); // Include t since it's used inside the effect

    const handleDelete = async (id) => {
        if (window.confirm(t('confirmDeleteCategory'))) {
            const deletePromise = new Promise(async (resolve, reject) => {
                try {
                    setDeleting(id);
                    await axios.delete(`/categories/${id}`);
                    // Update categories state directly
                    setCategories(prevCategories => prevCategories.filter(category => category.id !== id));
                    resolve();
                } catch (err) {
                    reject(new Error(err.response?.data?.message || t('errorDeletingCategory')));
                } finally {
                    setDeleting(null);
                }
            });

            toast.promise(deletePromise, {
                loading: t('deletingCategory'),
                success: t('categoryDeleted'),
                error: (err) => err.message,
            });
        }
    };

    const renderIcon = (iconName) => {
        const IconComponent = iconComponents[iconName] || FaQuestionCircle;
        return <IconComponent className="w-6 h-6 text-primary" />;
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const filteredCategories = useMemo(() => {
        return categories.filter(category => {
            const matchesSearch = filters.search === '' ||
                category.name.toLowerCase().includes(filters.search.toLowerCase()) ||
                category.name_ar.includes(filters.search);
            
            const matchesStatus = filters.status === 'all' ||
                (filters.status === 'active' && category.is_active) ||
                (filters.status === 'inactive' && !category.is_active);

            return matchesSearch && matchesStatus;
        }).sort((a, b) => {
            switch (filters.sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'order':
                    return a.sort_order - b.sort_order;
                default:
                    return 0;
            }
        });
    }, [categories, filters]);

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center h-[calc(100vh-4rem)]">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{t('categories')}</h1>
                        <p className="mt-1 text-sm text-gray-500">{t('categoriesDescription')}</p>
                    </div>
                    <Link
                        to="/admin/categories/create"
                        className={`inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg 
                            shadow-sm hover:bg-yellow-600 transform hover:translate-y-[-1px] transition-all duration-200
                            focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                        <FaPlus className="w-5 h-5" />
                        {t('addCategory')}
                    </Link>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-100">
                    <div className="flex flex-wrap gap-4 items-center">
                        <div className="w-96">
                            <div className="relative">
                                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                                    <FaSearch className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className={`block w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 
                                    focus:ring-primary focus:border-transparent ${
                                        isRTL ? 'pr-10 pl-3 text-right' : 'pl-10 pr-3 text-left'
                                    }`}
                                    placeholder={t('searchCategories')}
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <select
                                className={`w-44 py-2 px-3 border border-gray-200 rounded-lg focus:outline-none 
                                focus:ring-2 focus:ring-primary focus:border-transparent bg-white appearance-none
                                ${isRTL 
                                    ? 'pl-8 pr-3 bg-[length:1.5em_1.5em] bg-[left_0.5rem_center] rtl-select-bg' 
                                    : 'pr-8 pl-3 bg-[length:1.5em_1.5em] bg-[right_0.5rem_center] ltr-select-bg'
                                }`}
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`
                                }}
                            >
                                <option value="all">{t('allStatuses')}</option>
                                <option value="active">{t('active')}</option>
                                <option value="inactive">{t('inactive')}</option>
                            </select>
                            <select
                                className={`w-44 py-2 px-3 border border-gray-200 rounded-lg focus:outline-none 
                                focus:ring-2 focus:ring-primary focus:border-transparent bg-white appearance-none
                                ${isRTL 
                                    ? 'pl-8 pr-3 bg-[length:1.5em_1.5em] bg-[left_0.5rem_center] rtl-select-bg' 
                                    : 'pr-8 pl-3 bg-[length:1.5em_1.5em] bg-[right_0.5rem_center] ltr-select-bg'
                                }`}
                                value={filters.sortBy}
                                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`
                                }}
                            >
                                <option value="order">{t('sortByOrder')}</option>
                                <option value="name">{t('sortByName')}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                        {t('icon')}
                                    </th>
                                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('name')}
                                    </th>
                                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {t('nameArabic')}
                                    </th>
                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                        {t('status')}
                                    </th>
                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                                        {t('sortOrder')}
                                    </th>
                                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-left' : 'text-right'}`}>
                                        {t('actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredCategories.map((category) => (
                                    <tr key={category.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center justify-center">
                                                {renderIcon(category.icon)}
                                            </div>
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                                            {category.name}
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                                            {category.name_ar}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                category.is_active
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {category.is_active ? t('active') : t('inactive')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            {category.sort_order}
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-left' : 'text-right'}`}>
                                            <div className="flex items-center gap-3 justify-end">
                                                <Link
                                                    to={`/admin/categories/${category.id}/edit`}
                                                    className="text-yellow-600 hover:text-yellow-700 transition-colors p-2 hover:bg-yellow-50 rounded-full"
                                                    title={t('edit')}
                                                >
                                                    <FaEdit className="w-5 h-5" />
                                                </Link>
                                                <button
                                                    onClick={() => handleDelete(category.id)}
                                                    disabled={deleting === category.id}
                                                    className={`text-red-600 hover:text-red-700 transition-colors p-2 hover:bg-red-50 rounded-full ${
                                                        deleting === category.id ? 'opacity-50 cursor-not-allowed' : ''
                                                    }`}
                                                    title={t('delete')}
                                                >
                                                    <FaTrash className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredCategories.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                            {categories.length === 0 ? t('noCategoriesFound') : t('noMatchingCategories')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default CategoryIndex;
